import {
  TILE_SIZE,
  COLORS,
  PATH_WIDTH,
  FLAG_DATES,
  FLAG_WIDTH,
  FLAG_HEIGHT,
  FLAG_POLE_HEIGHT,
  FLAG_POLE_WIDTH,
  FLAG_OFFSET,
} from "../constants";
import type { PathSegment } from "./paths";

export const drawGrassTile = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  variant: number = 0
) => {
  // Base grass color
  ctx.fillStyle = COLORS.GRASS_BASE;
  ctx.fillRect(x, y, TILE_SIZE, TILE_SIZE);

  // Add texture patterns based on variant
  ctx.fillStyle = COLORS.GRASS_DARK;
  if (variant === 0) {
    // Light spots
    ctx.fillRect(x + 4, y + 4, 3, 3);
    ctx.fillRect(x + 20, y + 12, 2, 2);
    ctx.fillRect(x + 12, y + 20, 3, 3);
  } else if (variant === 1) {
    // Different pattern
    ctx.fillRect(x + 8, y + 8, 2, 2);
    ctx.fillRect(x + 18, y + 18, 2, 2);
    ctx.fillRect(x + 6, y + 22, 3, 2);
  } else if (variant === 2) {
    // Another pattern
    ctx.fillRect(x + 10, y + 6, 2, 2);
    ctx.fillRect(x + 22, y + 14, 2, 2);
    ctx.fillRect(x + 4, y + 18, 2, 3);
  } else {
    // Darker spots
    ctx.fillStyle = COLORS.GRASS_DARKER;
    ctx.fillRect(x + 6, y + 10, 2, 2);
    ctx.fillRect(x + 16, y + 6, 2, 2);
    ctx.fillRect(x + 24, y + 22, 2, 2);
  }

};

export const drawDirtTile = (ctx: CanvasRenderingContext2D, x: number, y: number) => {
  // Base dirt color
  ctx.fillStyle = COLORS.DIRT_BASE;
  ctx.fillRect(x, y, TILE_SIZE, TILE_SIZE);

  // Add texture
  ctx.fillStyle = COLORS.DIRT_DARK;
  ctx.fillRect(x + 2, y + 2, 4, 4);
  ctx.fillRect(x + 20, y + 8, 3, 3);
  ctx.fillRect(x + 8, y + 18, 4, 4);
  ctx.fillRect(x + 24, y + 22, 3, 3);

  // Lighter spots
  ctx.fillStyle = COLORS.DIRT_LIGHT;
  ctx.fillRect(x + 12, y + 6, 2, 2);
  ctx.fillRect(x + 6, y + 14, 2, 2);
  ctx.fillRect(x + 18, y + 16, 2, 2);

};

export const drawHeartTile = (ctx: CanvasRenderingContext2D, x: number, y: number) => {
  ctx.fillStyle = COLORS.HEART_RED;
  ctx.fillRect(x, y, TILE_SIZE, TILE_SIZE);

  // Add slight variation for depth
  ctx.fillStyle = COLORS.HEART_DARK_RED;
  ctx.fillRect(x + 2, y + 2, TILE_SIZE - 4, TILE_SIZE - 4);

};

export const drawTree = (ctx: CanvasRenderingContext2D, x: number, y: number, type: number = 0, zoom: number = 1, wiggleAngle: number = 0) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(zoom, zoom);
  // Apply wiggle rotation (rotate around bottom center - trees are drawn with base at y=0)
  if (wiggleAngle !== 0) {
    ctx.rotate(wiggleAngle);
  }
  if (type === 0) {
    // Christmas tree (4x sizes)
    // Draw tree trunk
    ctx.fillStyle = "#8b4513";
    ctx.fillRect(-12, -80, 24, 80);

    // Draw tree layers (triangles)
    ctx.fillStyle = "#228b22";
    // Bottom layer
    ctx.beginPath();
    ctx.moveTo(0, -120);
    ctx.lineTo(-60, -40);
    ctx.lineTo(60, -40);
    ctx.closePath();
    ctx.fill();

    // Middle layer
    ctx.beginPath();
    ctx.moveTo(0, -160);
    ctx.lineTo(-48, -80);
    ctx.lineTo(48, -80);
    ctx.closePath();
    ctx.fill();

    // Top layer
    ctx.beginPath();
    ctx.moveTo(0, -200);
    ctx.lineTo(-32, -120);
    ctx.lineTo(32, -120);
    ctx.closePath();
    ctx.fill();

    // Star on top
    ctx.fillStyle = "#ffd700";
    ctx.beginPath();
    ctx.moveTo(0, -220);
    ctx.lineTo(-8, -200);
    ctx.lineTo(8, -200);
    ctx.closePath();
    ctx.fill();
  } else {
    // Regular tree (type 1) (4x sizes)
    // Draw tree trunk
    ctx.fillStyle = "#654321";
    ctx.fillRect(-16, -100, 32, 100);

    // Draw foliage (circular/oval shape)
    ctx.fillStyle = "#2d5016";
    ctx.beginPath();
    ctx.ellipse(0, -120, 72, 88, 0, 0, Math.PI * 2);
    ctx.fill();

    // Add some texture
    ctx.fillStyle = "#3a6b1f";
    ctx.beginPath();
    ctx.ellipse(-32, -128, 32, 40, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(32, -112, 28, 36, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
};

export const drawRock = (ctx: CanvasRenderingContext2D, x: number, y: number, rockType: number = 0, zoom: number = 1) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(zoom, zoom);

  if (rockType === 0) {
    // Original rock (4x sizes)
    // Main rock body
    ctx.fillStyle = "#696969";
    ctx.beginPath();
    ctx.ellipse(0, 0, 64, 48, 0.3, 0, Math.PI * 2);
    ctx.fill();

    // Darker shadows
    ctx.fillStyle = "#555555";
    ctx.beginPath();
    ctx.ellipse(-24, 16, 32, 24, 0.2, 0, Math.PI * 2);
    ctx.fill();

    // Lighter highlights
    ctx.fillStyle = "#808080";
    ctx.beginPath();
    ctx.ellipse(24, -16, 24, 16, -0.2, 0, Math.PI * 2);
    ctx.fill();

    // Add some texture/details
    ctx.fillStyle = "#5a5a5a";
    ctx.beginPath();
    ctx.ellipse(-16, 4, 12, 8, 0.1, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = "#7a7a7a";
    ctx.beginPath();
    ctx.ellipse(16, -8, 12, 8, -0.1, 0, Math.PI * 2);
    ctx.fill();
  } else {
    // New rock type - smaller, darker, more angular (4x sizes)
    // Main body - darker gray
    ctx.fillStyle = "#4a4a4a";
    ctx.beginPath();
    ctx.ellipse(0, 0, 48, 36, -0.2, 0, Math.PI * 2);
    ctx.fill();

    // Darker base
    ctx.fillStyle = "#3a3a3a";
    ctx.beginPath();
    ctx.ellipse(-16, 20, 28, 20, 0.1, 0, Math.PI * 2);
    ctx.fill();

    // Lighter top
    ctx.fillStyle = "#5a5a5a";
    ctx.beginPath();
    ctx.ellipse(12, -18, 20, 16, -0.15, 0, Math.PI * 2);
    ctx.fill();

    // Angular details
    ctx.fillStyle = "#353535";
    ctx.beginPath();
    ctx.moveTo(-20, -10);
    ctx.lineTo(-30, 5);
    ctx.lineTo(-15, 12);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = "#555555";
    ctx.beginPath();
    ctx.moveTo(18, -8);
    ctx.lineTo(28, 8);
    ctx.lineTo(12, 15);
    ctx.closePath();
    ctx.fill();
  }

  ctx.restore();
};

export const drawDecorativeGrass = (ctx: CanvasRenderingContext2D, x: number, y: number, zoom: number = 1, wiggleAngle: number = 0) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(zoom, zoom);
  // Apply wiggle rotation
  if (wiggleAngle !== 0) {
    ctx.rotate(wiggleAngle);
  }

  // Draw larger, more visible grass tuft (4x sizes)
  ctx.fillStyle = "#5a9a2f";
  // Left blade (larger)
  ctx.beginPath();
  ctx.moveTo(-16, 0);
  ctx.quadraticCurveTo(-24, -64, -8, -96);
  ctx.quadraticCurveTo(0, -64, -16, 0);
  ctx.fill();

  // Center blade (tallest)
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.quadraticCurveTo(0, -80, 0, -112);
  ctx.quadraticCurveTo(8, -80, 0, 0);
  ctx.fill();

  // Right blade (larger)
  ctx.beginPath();
  ctx.moveTo(16, 0);
  ctx.quadraticCurveTo(24, -64, 8, -96);
  ctx.quadraticCurveTo(0, -64, 16, 0);
  ctx.fill();

  // Add more smaller blades for fullness
  ctx.fillStyle = "#6ab83d";
  // Left side blades
  ctx.beginPath();
  ctx.moveTo(-8, 0);
  ctx.quadraticCurveTo(-12, -48, -4, -72);
  ctx.quadraticCurveTo(0, -48, -8, 0);
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(-12, 0);
  ctx.quadraticCurveTo(-16, -56, -8, -80);
  ctx.quadraticCurveTo(-4, -56, -12, 0);
  ctx.fill();

  // Right side blades
  ctx.beginPath();
  ctx.moveTo(8, 0);
  ctx.quadraticCurveTo(12, -48, 4, -72);
  ctx.quadraticCurveTo(0, -48, 8, 0);
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(12, 0);
  ctx.quadraticCurveTo(16, -56, 8, -80);
  ctx.quadraticCurveTo(4, -56, 12, 0);
  ctx.fill();

  // Add a few more small accent blades
  ctx.fillStyle = "#4a7a1f";
  ctx.beginPath();
  ctx.moveTo(-4, 0);
  ctx.quadraticCurveTo(-6, -40, -2, -60);
  ctx.quadraticCurveTo(0, -40, -4, 0);
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(4, 0);
  ctx.quadraticCurveTo(6, -40, 2, -60);
  ctx.quadraticCurveTo(0, -40, 4, 0);
  ctx.fill();

  ctx.restore();
};

export const drawFlower = (ctx: CanvasRenderingContext2D, x: number, y: number, flowerType: number = 0, zoom: number = 1) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(zoom, zoom);

  if (flowerType === 0) {
    // Flower type 1 - Daisy-like (4x sizes)
    // Center
    ctx.fillStyle = "#ffd700"; // Yellow center
    ctx.beginPath();
    ctx.arc(0, 0, 8, 0, Math.PI * 2);
    ctx.fill();

    // Petals (8 petals)
    ctx.fillStyle = "#ffffff"; // White petals
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const petalX = Math.cos(angle) * 16;
      const petalY = Math.sin(angle) * 16;
      ctx.beginPath();
      ctx.ellipse(petalX, petalY, 8, 12, angle, 0, Math.PI * 2);
      ctx.fill();
    }

    // Stem
    ctx.fillStyle = "#228b22"; // Green stem
    ctx.fillRect(-2, 20, 4, 16);
  } else {
    // Flower type 2 - Rose-like (4x sizes)
    // Outer petals (darker)
    ctx.fillStyle = "#ff6b6b"; // Red
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const petalX = Math.cos(angle) * 12;
      const petalY = Math.sin(angle) * 12;
      ctx.beginPath();
      ctx.ellipse(petalX, petalY, 10, 14, angle + 0.3, 0, Math.PI * 2);
      ctx.fill();
    }

    // Inner petals (lighter)
    ctx.fillStyle = "#ff8e8e"; // Light red
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * Math.PI * 2;
      const petalX = Math.cos(angle) * 6;
      const petalY = Math.sin(angle) * 6;
      ctx.beginPath();
      ctx.ellipse(petalX, petalY, 6, 8, angle, 0, Math.PI * 2);
      ctx.fill();
    }

    // Center
    ctx.fillStyle = "#ffd700"; // Yellow center
    ctx.beginPath();
    ctx.arc(0, 0, 6, 0, Math.PI * 2);
    ctx.fill();

    // Stem with leaves
    ctx.fillStyle = "#228b22"; // Green stem
    ctx.fillRect(-2, 18, 4, 20);
    
    // Leaves
    ctx.fillStyle = "#32cd32"; // Light green leaves
    ctx.beginPath();
    ctx.ellipse(-8, 24, 6, 10, -0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(8, 28, 6, 10, 0.5, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
};

export const drawCar = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  angle: number,
  carW: number,
  carH: number,
  carImage: HTMLImageElement | null
) => {
  if (!carImage || !carImage.complete) {
    // Fallback: draw a simple rectangle if image isn't loaded
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.fillStyle = "#1a1a1a";
    ctx.fillRect(-carW / 2, -carH / 2, carW, carH);
    ctx.restore();
    return;
  }

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  
  // Draw the car image, centered and scaled to match car dimensions
  ctx.drawImage(carImage, -carW / 2, -carH / 2, carW, carH);
  
  ctx.restore();
};

export const drawBuilding = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  buildingIndex: number,
  zoom: number = 1
) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(zoom, zoom);
  
  // Draw flag pole
  ctx.fillStyle = "#8b7355"; // Brown pole
  ctx.fillRect(-FLAG_POLE_WIDTH / 2, -FLAG_POLE_HEIGHT, FLAG_POLE_WIDTH, FLAG_POLE_HEIGHT);
  
  // Draw triangle flag - pointed in the middle (pennant shape)
  if (buildingIndex === -1) {
    // Starting line flag - rectangular white and black checkered pattern
    const checkerSize = 30; // Size of each checker square
    const flagLeft = FLAG_POLE_WIDTH / 2;
    const flagTop = -FLAG_POLE_HEIGHT + FLAG_OFFSET;
    const flagBottom = -FLAG_POLE_HEIGHT + FLAG_OFFSET + FLAG_HEIGHT;
    const flagRight = flagLeft + FLAG_WIDTH;
    
    // Draw rectangular checkered pattern
    const numRows = Math.ceil(FLAG_HEIGHT / checkerSize);
    const numCols = Math.ceil(FLAG_WIDTH / checkerSize);
    
    for (let row = 0; row < numRows; row++) {
      const y1 = flagTop + (row * FLAG_HEIGHT / numRows);
      const y2 = flagTop + ((row + 1) * FLAG_HEIGHT / numRows);
      
      for (let col = 0; col < numCols; col++) {
        const x1 = flagLeft + (col * FLAG_WIDTH / numCols);
        const x2 = flagLeft + ((col + 1) * FLAG_WIDTH / numCols);
        
        // Alternate colors in checkerboard pattern
        const isBlack = (row + col) % 2 === 0;
        ctx.fillStyle = isBlack ? "#000000" : "#ffffff";
        
        // Draw rectangle for this checker square
        ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
      }
    }
    
    // Add flag border
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 4;
    ctx.strokeRect(flagLeft, flagTop, FLAG_WIDTH, FLAG_HEIGHT);
  } else {
    // Regular flag - red color
    ctx.fillStyle = "#ff0000"; // Red
    
    ctx.beginPath();
    ctx.moveTo(FLAG_POLE_WIDTH / 2, -FLAG_POLE_HEIGHT + FLAG_OFFSET); // Top point (at pole)
    ctx.lineTo(FLAG_POLE_WIDTH / 2 + FLAG_WIDTH, -FLAG_POLE_HEIGHT + FLAG_OFFSET + FLAG_HEIGHT / 2); // Right point (middle, extends outward)
    ctx.lineTo(FLAG_POLE_WIDTH / 2, -FLAG_POLE_HEIGHT + FLAG_OFFSET + FLAG_HEIGHT); // Bottom point (at pole)
    ctx.closePath();
    ctx.fill();
    
    // Add flag border
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 4;
    ctx.stroke();
  }
  
  // Draw text right below the flag (2x bigger)
  ctx.fillStyle = "#ffffff"; // White text
  ctx.font = "bold 96px sans-serif"; // 2x bigger (was 48px, now 96px)
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  
  if (buildingIndex === -1) {
    // Starting line flag - show "START"
    ctx.fillText("START", 0, -FLAG_POLE_HEIGHT + FLAG_OFFSET + FLAG_HEIGHT + 250);
  } else {
    // Regular flag - show date
    const date = FLAG_DATES[buildingIndex] || "01-01-2025";
    ctx.fillText(date, 0, -FLAG_POLE_HEIGHT + FLAG_OFFSET + FLAG_HEIGHT + 250);
  }
  
  ctx.restore();
};

export const drawPath = (
  ctx: CanvasRenderingContext2D,
  segment: PathSegment,
  cameraX: number,
  cameraY: number,
  zoom: number = 1
) => {
  const screenX1 = (segment.x1 - cameraX) * zoom;
  const screenY1 = (segment.y1 - cameraY) * zoom;
  const screenX2 = (segment.x2 - cameraX) * zoom;
  const screenY2 = (segment.y2 - cameraY) * zoom;

  // Only draw if segment is visible on screen
  const padding = PATH_WIDTH;
  if (
    (screenX1 > -padding && screenX1 < ctx.canvas.width + padding) ||
    (screenX2 > -padding && screenX2 < ctx.canvas.width + padding) ||
    (screenY1 > -padding && screenY1 < ctx.canvas.height + padding) ||
    (screenY2 > -padding && screenY2 < ctx.canvas.height + padding)
  ) {
    ctx.save();
    
    // Draw base path
    ctx.strokeStyle = COLORS.DIRT_BASE;
    ctx.lineWidth = PATH_WIDTH * zoom;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(screenX1, screenY1);
    ctx.lineTo(screenX2, screenY2);
    ctx.stroke();
    
    // Add random light colored gravel along the path
    const dx = screenX2 - screenX1;
    const dy = screenY2 - screenY1;
    const segmentLength = Math.sqrt(dx * dx + dy * dy);
    const perpX = -dy / segmentLength;
    const perpY = dx / segmentLength;
    
    // Calculate number of gravel pieces based on segment length (much higher density)
    const gravelDensity = 0.8; // Gravel pieces per pixel (increased from 0.15)
    const numGravel = Math.floor(segmentLength * gravelDensity);
    
    // Use segment coordinates for seeded random (consistent gravel positions)
    const seed = Math.floor(segment.x1 * 1000 + segment.y1 * 1000 + segment.x2 * 1000 + segment.y2 * 1000);
    let random = seed;
    const seededRandom = () => {
      random = (random * 9301 + 49297) % 233280;
      return random / 233280;
    };
    
    // Light colored gravel (lighter than dirt base for better visibility)
    const gravelColors = [
      "#d4c5a9", // Very light tan
      "#e5d4b8", // Light beige
      "#c9b89a", // Light brown
      "#f0e6d2", // Almost white
    ];
    
    for (let i = 0; i < numGravel; i++) {
      const t = seededRandom(); // Position along segment (0 to 1)
      const offset = (seededRandom() - 0.5) * PATH_WIDTH * zoom * 0.7; // Random offset perpendicular to path
      
      const gravelX = screenX1 + dx * t + perpX * offset;
      const gravelY = screenY1 + dy * t + perpY * offset;
      
      // Random size for gravel (2-5 pixels, larger and more visible)
      const gravelSize = 2 + seededRandom() * 3;
      
      // Use random color from gravel colors array
      ctx.fillStyle = gravelColors[Math.floor(seededRandom() * gravelColors.length)];
      
      // Draw small gravel piece as a circle or small ellipse
      ctx.beginPath();
      if (seededRandom() > 0.4) {
        // Circle (60% chance)
        ctx.arc(gravelX, gravelY, gravelSize * zoom, 0, Math.PI * 2);
      } else {
        // Small ellipse for variety (40% chance)
        const angle = seededRandom() * Math.PI * 2;
        ctx.ellipse(gravelX, gravelY, gravelSize * zoom, gravelSize * zoom * 0.6, angle, 0, Math.PI * 2);
      }
      ctx.fill();
      
      // Add a subtle highlight to some gravel pieces for depth
      if (seededRandom() > 0.7) {
        ctx.fillStyle = "#ffffff";
        ctx.globalAlpha = 0.3;
        ctx.beginPath();
        ctx.arc(gravelX - gravelSize * zoom * 0.3, gravelY - gravelSize * zoom * 0.3, gravelSize * zoom * 0.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1.0;
      }
    }
    
    ctx.restore();
  }
};

// Draw pond tile (water tile)
export const drawPondTile = (ctx: CanvasRenderingContext2D, x: number, y: number, variant: number = 0) => {
  // Base water color
  ctx.fillStyle = "#1e3a8a";
  ctx.fillRect(x, y, TILE_SIZE, TILE_SIZE);

  // Add water texture patterns based on variant
  ctx.fillStyle = "#3b82f6";
  if (variant === 0) {
    // Light ripples
    ctx.fillRect(x + 4, y + 4, 6, 3);
    ctx.fillRect(x + 18, y + 12, 4, 4);
    ctx.fillRect(x + 10, y + 20, 5, 4);
  } else if (variant === 1) {
    // Different pattern
    ctx.fillRect(x + 6, y + 8, 4, 4);
    ctx.fillRect(x + 20, y + 16, 3, 3);
    ctx.fillRect(x + 8, y + 22, 4, 3);
  } else if (variant === 2) {
    // Another pattern
    ctx.fillRect(x + 8, y + 6, 3, 3);
    ctx.fillRect(x + 16, y + 14, 4, 4);
    ctx.fillRect(x + 4, y + 18, 5, 3);
  } else {
    // Darker water
    ctx.fillStyle = "#1e40af";
    ctx.fillRect(x + 6, y + 10, 3, 3);
    ctx.fillRect(x + 18, y + 6, 3, 3);
    ctx.fillRect(x + 22, y + 20, 3, 3);
  }

};

